/**
 * Error reporting boundary.
 *
 * REWRITTEN 2026-08-23 after a cross-model adversarial review found three
 * confirmed escape paths in the first version. All three shared one root cause:
 * the first version *edited* the event Sentry built, which is a denylist. It
 * protected the fields I thought of. This version *constructs* the event from
 * nothing, which is an allowlist — a field I have never heard of cannot leak,
 * because nothing copies it.
 *
 * WHY THE BAR IS THIS HIGH. Sentry is a US-region processor and there is no
 * data-processing agreement with them, by decision. So the design target is not
 * "scrub PHI" — it is that Sentry receiving our data is harmless. Anything that
 * requires me to have anticipated a field name fails that target.
 *
 * WHAT THE REVIEW FOUND, each verified independently before rewriting:
 *
 *  1. `integrations: []` does NOT disable defaults. @sentry/core does
 *     `[...defaultIntegrations, ...userIntegrations]`, so Breadcrumbs,
 *     GlobalHandlers, HttpContext and BrowserSession were all live. DOM
 *     breadcrumbs serialise `aria-label`/`title`/`alt` — which in this app carry
 *     patient names — and navigation breadcrumbs put raw URLs in `data.from` and
 *     `data.to`, which the old hook did not touch (it scrubbed only `data.url`).
 *     Fixed with `defaultIntegrations: false` and an explicit short list.
 *
 *  2. `beforeSend` left `exception.values[].value` intact. That is the error
 *     MESSAGE, and PROD-2022 established that this codebase puts clinical text
 *     into exception messages. I documented that defect this morning and then
 *     shipped code with the same hole.
 *
 *  3. `scrubUrl` was shape-based, so it missed everything that does not look
 *     like a UUID: Emirates IDs (`784-1990-1234567-1`), alphanumeric and short
 *     MRNs, names, percent-encoded values. Measured: 5 of 7 real identifier
 *     shapes passed straight through.
 *
 * And the tests passed while all three were true, because they only exercised
 * the URL helper with cases I chose myself.
 */

import {
  init,
  dedupeIntegration,
  functionToStringIntegration,
  globalHandlersIntegration,
  type ErrorEvent,
} from '@sentry/react'

/**
 * The ONLY path segments allowed through verbatim. Anything absent from this
 * list is redacted, whatever it looks like.
 *
 * This is the inversion that matters. A shape-based rule has to predict the
 * format of every identifier the platform will ever mint. An allowlist has to
 * predict the format of nothing: a new id shape, a name, an encoded value and a
 * field I have not imagined all redact by default. The cost is that a new route
 * segment reads as `:x` until someone adds it here — a legible, harmless cost.
 */
const SAFE_SEGMENTS = new Set([
  'api', 'v1', 'v2', 'connect', 'health',
  'patients', 'patient', 'encounters', 'encounter', 'appointments', 'appointment',
  'practitioners', 'practitioner', 'providers', 'provider', 'doctor', 'doctors',
  'organizations', 'organization', 'practice', 'practices', 'clinics',
  'billing', 'claims', 'invoice', 'invoices', 'payments',
  'master', 'lookup', 'lookups', 'specialities', 'specialties',
  'questionbank', 'templates', 'template', 'forms', 'questionnaire',
  'workflows', 'workflow', 'timeline', 'documents', 'attachments',
  'allergies', 'medications', 'diagnoses', 'observations', 'labs', 'vitals',
  'notes', 'orders', 'referrals', 'schedule', 'slots', 'calendarslots',
  'search', 'me', 'login', 'logout', 'token', 'refresh', 'ambient', 'stream',
])

/**
 * Reduce a URL to route shape only.
 *
 * Every segment not on the allowlist becomes `:x`. The query string and fragment
 * are dropped entirely — filtering them by key would reintroduce the denylist
 * problem one level down.
 */
export function scrubUrl(raw: string): string {
  if (!raw) return raw
  try {
    const u = new URL(raw, 'https://scrubbed.invalid')
    const path = u.pathname
      .split('/')
      .map((seg) => {
        if (!seg) return seg
        // Decode first: an encoded name must not survive by hiding as %20.
        let decoded = seg
        try { decoded = decodeURIComponent(seg) } catch { /* keep raw */ }
        return SAFE_SEGMENTS.has(decoded.toLowerCase()) ? decoded : ':x'
      })
      .join('/')
    const isAbsolute = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw)
    return isAbsolute ? `${u.origin}${path}` : path
  } catch {
    return '[unparseable-url]'
  }
}

/** Frame filenames are bundle paths, but a query string can ride along. */
function scrubFrameFile(f: string | undefined): string | undefined {
  if (!f) return f
  return f.split('?')[0].split('#')[0]
}

/**
 * Build the outbound event from nothing.
 *
 * Anything not explicitly copied here does not leave. That includes `message`,
 * `logentry`, `extra`, `contexts`, `tags`, `threads`, `breadcrumbs`, `user`,
 * `request` and every field added by a future SDK version.
 *
 * Exception VALUES are dropped and replaced with the length of what was
 * discarded. The type and the stack are what localise a bug; the message is
 * where this codebase has been putting clinical text.
 */
export function buildSafeEvent(event: ErrorEvent): ErrorEvent {
  const safe: Record<string, unknown> = {
    event_id: event.event_id,
    timestamp: event.timestamp,
    platform: event.platform,
    level: event.level,
    release: event.release,
    environment: event.environment,
    sdk: event.sdk,
    transaction: event.transaction ? scrubUrl(event.transaction) : undefined,
  }

  const values = event.exception?.values
  if (values?.length) {
    safe.exception = {
      values: values.map((v) => ({
        type: v.type,
        value:
          typeof v.value === 'string' && v.value.length > 0
            ? `[message withheld — ${v.value.length} chars]`
            : undefined,
        mechanism: v.mechanism ? { type: v.mechanism.type, handled: v.mechanism.handled } : undefined,
        stacktrace: v.stacktrace
          ? {
              frames: (v.stacktrace.frames ?? []).map((fr) => ({
                filename: scrubFrameFile(fr.filename),
                function: fr.function,
                lineno: fr.lineno,
                colno: fr.colno,
                in_app: fr.in_app,
              })),
            }
          : undefined,
      })),
    }
  }
  return safe as unknown as ErrorEvent
}

/**
 * The init options, exported so they can be ASSERTED rather than described.
 *
 * A bundle grep cannot tell a registered integration from a bundled symbol —
 * `browserSessionIntegration` appears in the built output either way, because
 * the SDK references it in its own defaults list. So the guarantee lives here,
 * where a test can read it: `defaultIntegrations: false` and an explicit list of
 * three.
 */
export function buildInitOptions(dsn: string, environment?: string, release?: string) {
  return {
    dsn,
    environment,
    release,
    sendDefaultPii: false,

    // The load-bearing line. Without it the SDK appends its defaults to whatever
    // is listed here, and Breadcrumbs / HttpContext / BrowserSession come back.
    defaultIntegrations: false as const,
    integrations: [
      globalHandlersIntegration({ onerror: true, onunhandledrejection: true }),
      dedupeIntegration(),
      functionToStringIntegration(),
    ],

    // Breadcrumbs are off at the source rather than sanitised in a hook. They
    // were the widest carrier: DOM breadcrumbs serialise element labels, and
    // navigation breadcrumbs carry raw URLs in fields a hook has to know about.
    maxBreadcrumbs: 0,

    // No tracing, no profiling, no replay.
    tracesSampleRate: 0,
    // NOTE: there is deliberately no `autoSessionTracking` here — it does not
    // exist in Sentry 10. Sessions come from `browserSessionIntegration`, which
    // is a DEFAULT, so `defaultIntegrations: false` above is what disables them.
    // Worth stating: session envelopes never pass through beforeSend, so a
    // stable id inside one could not be redacted even in principle.

    ignoreErrors: [
      'ResizeObserver loop completed with undelivered notifications',
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
      'AbortError',
      /^Loading chunk \d+ failed/,
    ],
    denyUrls: [/extensions\//i, /^chrome:\/\//i, /^moz-extension:\/\//i],

    beforeSend(event: ErrorEvent) {
      return buildSafeEvent(event)
    },
  }
}

export function initObservability(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) return

  const host = window.location.hostname
  if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local')) return

  init(
    buildInitOptions(
      dsn,
      import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
      import.meta.env.VITE_APP_VERSION,
    ),
  )
}

/**
 * Intentionally NOT provided: a setUser helper.
 *
 * The first version exported one that attached the clinician's internal id. The
 * review's point stands — a stable identifier for a named person, sitting with a
 * processor we have no agreement with, is the thing we are trying to stop, and
 * "it is only an id" is the argument that got us `username` and `email` on the
 * Flutter side. Correlate through our own systems, where the lookup is audited.
 */
